import { Injectable } from '@angular/core'
import { BYPASS_403_REDIRECT } from '@ftr/api-core'
import { RegionalApiClientFactory, RegionalApiClientSetupService } from '@ftr/api-regional'
import { ApiClient, SerializedHttpParams, isForbiddenError, serializeHttpParams } from '@ftr/api-shared'
import { SttSpeaker, SttUtteranceWithContent, addSpeakerToUtterances } from '@ftr/contracts/regional-api/stt'
import { DataStore, Uuid } from '@ftr/contracts/type/shared'
import { ApiResult, attemptRecover, mapData } from '@ftr/foundation'
import { LocalDateTime, ZoneId, convert } from '@js-joda/core'
import { switchMap } from 'rxjs'
import { RealTimeSttWithRemarks } from '../../types'
import { utterancesToRemarks } from '../../utils'

@Injectable({
  providedIn: 'root',
})
export class RealTimeCourtRecordingSttService extends RegionalApiClientSetupService {
  override readonly regionalApiPath = 'stt'

  constructor(regionalApiClientFactory: RegionalApiClientFactory) {
    super(regionalApiClientFactory)
  }

  getSttWithRemarks(
    courtSystemId: Uuid,
    dataStore: DataStore,
    recordingId: Uuid,
    hasStt: boolean,
    start?: LocalDateTime,
    end?: LocalDateTime,
  ): ApiResult<RealTimeSttWithRemarks> {
    return this.regionalApiClient(this.regionalApiPath, courtSystemId).pipe(
      switchMap(apiClient => {
        if (!hasStt) {
          return ApiResult.success({ recordingId, remarks: [] })
        }

        const params = serializeHttpParams({
          start: start ? convert(start.atZone(ZoneId.UTC)).toEpochMilli().toString() : '',
          end: end ? convert(end.atZone(ZoneId.UTC)).toEpochMilli().toString() : '',
        })

        return ApiResult.combine([
          this.getSpeakers(apiClient, courtSystemId, recordingId, dataStore),
          this.getUtterances(apiClient, courtSystemId, recordingId, params, dataStore),
        ]).pipe(
          mapData(([speakers, utterances]) => {
            return {
              recordingId,
              remarks: utterancesToRemarks(addSpeakerToUtterances(utterances, speakers)),
            }
          }),
        )
      }),
      attemptRecover(error => {
        if (isForbiddenError(error)) {
          return ApiResult.success({ recordingId, remarks: [] })
        }
        return ApiResult.failure(error)
      }),
    )
  }

  private getSpeakers(
    apiClient: ApiClient,
    courtSystemId: Uuid,
    recordingId: Uuid,
    dataStore: DataStore,
  ): ApiResult<SttSpeaker[]> {
    return apiClient.get({
      path: `${recordingId}/speakers`,
      headers: {
        courtSystemId,
        dataStore,
        // Disables the sneaky forbidden redirect in the `auth-redirect.interceptor.ts`
        [BYPASS_403_REDIRECT]: 'true',
      },
    })
  }

  private getUtterances(
    apiClient: ApiClient,
    courtSystemId: Uuid,
    recordingId: Uuid,
    params: SerializedHttpParams,
    dataStore: DataStore,
  ): ApiResult<SttUtteranceWithContent[]> {
    return apiClient.get({
      path: `${recordingId}/utterances`,
      headers: {
        courtSystemId,
        dataStore,
        // Disables the sneaky forbidden redirect in the `auth-redirect.interceptor.ts`
        [BYPASS_403_REDIRECT]: 'true',
      },
      params: serializeHttpParams(params),
    })
  }
}
